// Der einzige Weg, auf dem die Oberfläche mit dem Worker spricht. Eine Methode je Route, gruppiert
// wie die Routendateien: auth, me, t(tenantId), admin, dokumente.
//
// Regeln dieser Datei:
//  - Nur relative Pfade. Die Oberfläche kommt aus demselben Worker wie die API; nur dann schickt der
//    Browser den `Origin`-Header, den der CSRF-Wächter in app.ts prüft (setzen kann man ihn nicht,
//    `Origin` ist ein verbotener Header-Name).
//  - `credentials: 'include'`, weil die Sitzung in einem HttpOnly-Cookie steckt.
//  - Jede Mutation sendet JSON, notfalls `{}` — sonst antwortet der Wächter mit 415.
//  - Jeder 4xx/5xx wird zu einem ApiError mit dem deutschen Satz des Servers. Die Oberfläche
//    formuliert einen Serverfehler nie um (parts4/00-contract.md).
//  - Antworttypen bilden die Server-Antworten ab; die Herkunft steht am jeweiligen Typ.
import type { InviteAccept, LoginCodeRequest, LoginVerify } from '../shared/schemas/auth'
import type {
  CancelBody, EntwurfBody, IssuerSnapshot, PayoutBody, RechnungenQuery, RecipientSnapshot, RejectBody,
  SubmitBody,
} from '../shared/schemas/billing'
import type {
  DryRunRuleBody, MatchTestBody, ReplaceTermsBody, UpsertProductBody, UpsertRuleBody,
} from '../shared/schemas/catalogue'
import type { ProfileCompleteness } from '../shared/schemas/contractor'
import type {
  AbschlussPatch, Adjustment, CreateDeal, CreateDealDirect, Dismiss, InboxQuery, ManualPayment,
  RefundDecision, WriteOff, ZahlungPatch,
} from '../shared/schemas/corrections'
import type { TestEmailInput } from '../shared/schemas/email'
import type { ExporteQuery, MonatsExport } from '../shared/schemas/export'
import type { ProfilPatch } from '../shared/schemas/profil'
import type {
  AuditQuery, CommissionsQuery, DashboardQuery, DealsQuery, MyCommissionsQuery, PaymentsQuery,
} from '../shared/schemas/reads'
import type { CreateTeamMemberBody, InviteTeamMemberBody, PatchTeamMemberBody } from '../shared/schemas/team'
import type {
  CreateTenantBody, InviteOwnerBody, LinkContractorBody, UpdateTenantBody,
} from '../shared/schemas/tenant'

// ---------------------------------------------------------------------------------------------------
// Fehler
// ---------------------------------------------------------------------------------------------------

/** Der Fehlerkörper jeder 4xx-Antwort (Plan-2-Contract): { error, message, field? }. */
export interface ApiFehlerKoerper {
  error: string
  message: string
  field?: string
}

export class ApiError extends Error {
  readonly status: number
  /** Der stabile snake_case-Code aus src/worker/http.ts — darauf darf ein Bildschirm verzweigen. */
  readonly error: string
  /** Das Feld, an dem der Fehler hängt, wenn der Server eines nennt. */
  readonly field: string | undefined

  constructor(status: number, error: string, message: string, field?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.error = error
    this.field = field
  }
}

export function istApiError(fehler: unknown): fehler is ApiError {
  return fehler instanceof ApiError
}

/**
 * Keine gültige Sitzung — daraufhin schickt der Router zur Anmeldung (auth.tsx).
 *
 * Der Status allein reicht dafür nicht: der Server antwortet an zwei Stellen mit 401. `requireSession`
 * (auth/middleware.ts) meldet `nicht_angemeldet`, `POST /api/auth/verify` meldet `code_ungueltig`
 * (auth/codes.ts), sobald ein Anmeldecode falsch, abgelaufen oder verbraucht ist. Nur der erste Fall
 * darf zur Anmeldung umleiten; sonst wirft ein Tippfehler im sechsstelligen Code den Nutzer aus dem
 * Anmeldeformular, statt ihm den Satz des Servers zu zeigen.
 */
export function istNichtAngemeldet(fehler: unknown): boolean {
  return fehler instanceof ApiError && fehler.status === 401 && fehler.error === 'nicht_angemeldet'
}

// ---------------------------------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------------------------------

type QueryWert = string | number | boolean | null | undefined
export type Query = Record<string, QueryWert>

function mitQuery(pfad: string, query?: Query): string {
  if (query === undefined) return pfad
  const parameter = new URLSearchParams()
  for (const [name, wert] of Object.entries(query)) {
    if (wert === undefined || wert === null || wert === '') continue
    parameter.set(name, String(wert))
  }
  const text = parameter.toString()
  return text === '' ? pfad : `${pfad}?${text}`
}

function teil(wert: string): string {
  return encodeURIComponent(wert)
}

/**
 * Ein Körper, der kein JSON ist, muss von einem Körper unterscheidbar bleiben, der `null` *ist* —
 * `JSON.parse('null')` liefert sonst dasselbe wie ein Fehlschlag. Daher dieses eigene Kennzeichen.
 */
const UNLESBAR = Symbol('unlesbar')

function gelesen(text: string): unknown {
  if (text === '') return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return UNLESBAR
  }
}

/**
 * Der eine Ort, an dem aus einem Fehlerkörper ein ApiError wird — für `anfrage` wie für
 * `dokumente.laden`. Zwei Kopien dieser Regel hieße, den Fehler-Contract zweimal nachzuziehen.
 */
function fehlerAus(status: number, daten: unknown, standardsatz: string): ApiError {
  const koerper = (daten === UNLESBAR || daten === null ? {} : daten) as Partial<ApiFehlerKoerper>
  return new ApiError(
    status,
    typeof koerper.error === 'string' ? koerper.error : 'unbekannt',
    typeof koerper.message === 'string' ? koerper.message : standardsatz,
    typeof koerper.field === 'string' ? koerper.field : undefined,
  )
}

async function anfrage<T>(
  methode: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  pfad: string,
  optionen: { query?: Query; body?: unknown } = {},
): Promise<T> {
  const kopfzeilen: Record<string, string> = { accept: 'application/json' }
  const init: RequestInit = { method: methode, credentials: 'include' }
  if (optionen.body !== undefined) {
    kopfzeilen['content-type'] = 'application/json'
    init.body = JSON.stringify(optionen.body)
  }
  init.headers = kopfzeilen

  const antwort = await fetch(mitQuery(pfad, optionen.query), init)
  const daten = gelesen(await antwort.text())

  if (!antwort.ok) throw fehlerAus(antwort.status, daten, 'Die Anfrage ist fehlgeschlagen.')
  // Eine 2xx-Antwort, die kein JSON ist, darf nicht als `null` durchgereicht werden: der Bildschirm
  // bekäme sonst ein `null` im Typ der Antwort und stürbe erst tief in einer Komponente.
  if (daten === UNLESBAR) {
    throw new ApiError(antwort.status, 'unlesbare_antwort', 'Die Antwort des Servers war unlesbar.')
  }
  return daten as T
}

const holen = <T>(pfad: string, query?: Query): Promise<T> => anfrage<T>('GET', pfad, { query })
/** POST sendet immer JSON — auch wenn es nichts zu sagen gibt, sonst antwortet der Wächter 415. */
const senden = <T>(pfad: string, body: unknown = {}, query?: Query): Promise<T> =>
  anfrage<T>('POST', pfad, { body, query })
const aendern = <T>(pfad: string, body: unknown): Promise<T> => anfrage<T>('PATCH', pfad, { body })
// Mit leerem JSON-Körper: der CSRF-Wächter des Workers verlangt bei jeder ändernden Methode einen
// JSON-Content-Type, auch bei DELETE (app.ts, MUTATING).
const loeschen = <T>(pfad: string): Promise<T> => anfrage<T>('DELETE', pfad, { body: {} })

// ---------------------------------------------------------------------------------------------------
// Antworttypen — Herkunft jeweils genannt. Sie bilden den Server ab; sie erfinden nichts.
// ---------------------------------------------------------------------------------------------------

/** routes/auth.ts, /verify und /me */
export interface Benutzer {
  id: string
  email: string
  display_name: string
  is_platform_admin: boolean
}

export interface SitzungTenant {
  id: string
  slug: string
  name: string
  role: string
}

export interface SitzungContractor {
  id: string
  display_name: string
  invoice_prefix: string
  verified_at: string | null
}

/** GET /api/auth/me */
export interface Sitzung {
  user: Benutzer
  tenants: SitzungTenant[]
  contractors: SitzungContractor[]
}

/** auth/invites.ts, AcceptResult */
export interface EinladungAuswahl {
  id: string
  display_name: string
  invoice_prefix: string
}

export type EinladungErgebnis =
  | { status: 'member'; tenant_id: string; role: 'owner' | 'admin' }
  | { status: 'contractor_linked'; tenant_id: string; tenant_contractor_id: string; contractor_id: string }
  | { status: 'contractor_claimed'; tenant_id: string; tenant_contractor_id: string; contractor_id: string }
  | { status: 'choose_contractor'; tenant_id: string; tenant_contractor_id: string; choices: EinladungAuswahl[] }

/** services/reads.ts, Dashboard (Datumsbasis jedes Eimers: spec §1.9) */
export interface MonatsWert {
  monat: string
  cents: number
}

export interface MonatsReihe {
  gesamt_cents: number
  ohne_datum_cents: number
  monate: MonatsWert[]
}

export interface ProvisionsMonat {
  monat: string
  faellig_cents: number
  ausgezahlt_cents: number
  offen_cents: number
}

export interface EmpfaengerSaldo {
  recipient_tc_id: string
  short_code: string
  display_name: string
  faellig_cents: number
  ausgezahlt_cents: number
  offen_cents: number
}

export interface KontrollZaehler {
  kind: string
  anzahl: number
}

export interface Dashboard {
  jahr: number
  auftragsvolumen: MonatsReihe
  cashflow_brutto: MonatsReihe
  netto_basis: MonatsReihe
  provisionen: {
    faellig_cents: number
    ausgezahlt_cents: number
    offen_cents: number
    monate: ProvisionsMonat[]
    empfaenger: EmpfaengerSaldo[]
  }
  kontrolle: KontrollZaehler[]
}

/** services/queue.ts, QueueItem */
/**
 * Was die Korrektur-Endpunkte wirklich antworten (services/corrections.ts). Die Bildschirme lesen
 * davon meist nichts — der Vertrag verlangt, die betroffenen Zeilen neu zu laden statt den Cache mit
 * einem geratenen Ergebnis zu flicken. Trotzdem stehen die echten Typen hier: `Promise<void>` war
 * eine Falschaussage, und wer künftig `recompute.ran` braucht (etwa um zu melden, dass der Lauf
 * gerade belegt war und die Verarbeitung nachzieht), fände sonst nichts vor.
 */
export interface NeuberechnungErgebnis {
  ran: boolean
  statements: number
  commissionsInserted: number
  commissionsVoided: number
  reversals: number
  eventsProcessed: number
}
export interface AbschlussErgebnis { deal: Record<string, unknown>; recompute: NeuberechnungErgebnis }
export interface ZahlungErgebnis { invoice: Record<string, unknown>; recompute: NeuberechnungErgebnis }
export interface AbschlussAusZahlungErgebnis {
  deal: Record<string, unknown>
  invoice: Record<string, unknown>
  recompute: NeuberechnungErgebnis
}
export interface RueckzahlungErgebnis {
  delta: Record<string, unknown>
  invoice: Record<string, unknown>
  recompute: NeuberechnungErgebnis
}
export interface HandZahlungErgebnis {
  event_id: string
  superseded_event_id: string | null
  recompute: NeuberechnungErgebnis
}
export interface KorrekturErgebnis { commission: Record<string, unknown> }
export interface AusbuchungErgebnis { original: Record<string, unknown>; offset: Record<string, unknown> }

export interface PruefenEintrag {
  kind: string
  subject_type: string
  subject_id: string
  since: string
  severity: number
  label: string
  customer_name: string | null
  invoice_number: string | null
  amount_cents: number | null
  deal_key: string | null
  note: string | null
  /**
   * Die Elternzeile, über die eine Korrektur adressiert wird: bei einer Rate die Zahlung
   * (`customer_invoice_id`), bei einer fehlenden Rolle der Abschluss (`deal_id`), bei einer
   * Rückbuchung der Empfänger. `null`, wenn die Zeile selbst schon das Ziel ist.
   */
  subject_parent_id: string | null
  role_code: string | null
  /** Bei einer Zahlung: das Produkt, das sie schon trägt — Vorgabe fürs „Closer zuordnen“ */
  product_id?: string | null
}

/** services/reads.ts, DealListRow */
export interface AbschlussZeile {
  id: string
  hl_deal_key: string | null
  hl_contact_id: string | null
  customer_name: string
  signed_at: string | null
  contract_value_cents: number | null
  status: string
  product_id: string | null
  product_code: string | null
  product_name: string | null
  closer_tc_id: string | null
  closer_short_code: string | null
  setter_tc_id: string | null
  setter_short_code: string | null
  host_tc_id: string | null
  host_short_code: string | null
  sonstige_tc_id: string | null
  sonstige_short_code: string | null
  host_rate_bp: number
  /** Lead selbst geholt (extern) oder von der Firma gestellt (intern); null = nicht angegeben, zählt als extern. */
  lead_source: 'intern' | 'extern' | null
  overhead_individual_cents: number
  notes: string | null
  version: number
  updated_at: string
}

/** services/reads.ts, PaymentListRow */
export interface ZahlungZeile {
  id: string
  invoice_number: string
  customer_name: string
  invoice_name: string
  match_status: string
  deal_id: string | null
  manual_deal_id: string | null
  detected_product_id: string | null
  manual_product_id: string | null
  processed_cumulative_cents: number
  last_reported_cumulative_cents: number
  latest_net_cents: number | null
  latest_gross_cents: number | null
  latest_status: string | null
  invoice_url: string | null
  updated_at: string
  letzte_zahlung: string | null
  raten: number
  summe_cents: number
}

/** services/reads.ts, CommissionListRow */
export interface ProvisionZeile {
  id: string
  ref_no: number
  role_code: string
  role_label: string
  leistung_text: string
  recipient_tc_id: string | null
  empfaenger_short_code: string | null
  empfaenger_name: string | null
  basis_cents: number
  rate_nr: number | null
  amount_cents: number
  kind: string
  status: string
  delta_id: string | null
  deal_id: string | null
  customer_name: string | null
  product_code: string | null
  effective_date: string | null
  paid_at: string | null
  note: string | null
  legacy_prov_id: string | null
  contractor_invoice_id: string | null
  created_at: string
  faellig_am: string
  /** Bestätigung durch den Auftraggeber (Migration 0008); null = noch nicht bestätigt */
  bestaetigt_at: string | null
}

/** services/reads.ts, AuditListRow */
export interface AuditZeile {
  id: string
  actor_type: string
  actor_user_id: string | null
  acting_contractor_id: string | null
  action: string
  entity_type: string
  entity_id: string
  before_json: string | null
  after_json: string | null
  reason: string | null
  request_id: string | null
  created_at: string
}

/** services/reads.ts, MyCommissionRow */
export interface MeineProvisionZeile {
  id: string
  tenant_id: string
  tenant_name: string
  ref_no: number
  recipient_tc_id: string | null
  empfaenger: string
  role_label: string
  leistung_text: string
  rate_nr: number | null
  amount_cents: number
  kind: string
  status: string
  customer_name: string | null
  product_code: string | null
  effective_date: string | null
  paid_at: string | null
  contractor_invoice_id: string | null
  created_at: string
}

/** services/reads.ts, MyPipelineRow */
export interface PipelineZeile {
  id: string
  tenant_id: string
  tenant_name: string
  customer_name: string
  hl_contact_id: string | null
  status: string
  signed_at: string | null
  contract_value_cents: number | null
  product_code: string | null
  gezahlt_cents: number
  letzter_zahlungseingang: string | null
  meine_rollen: string[]
}

/** services/profil.ts, OwnProfile */
export interface EigenesProfil {
  id: string
  display_name: string
  legal_name: string | null
  legal_form: string | null
  representative_name: string | null
  address: unknown
  country: string
  vat_mode: 'kleinunternehmer' | 'regelbesteuert' | 'reverse_charge'
  vat_id: string | null
  tax_number: string | null
  iban: string | null
  bic: string | null
  account_holder: string | null
  invoice_prefix: string
  verified_at: string | null
  vollstaendig: boolean
}

/** engine/types.ts, ProductRow + MatchTermRow (services/catalogue.ts, ProductWithTerms) */
export interface BegriffZeile {
  id: string
  tenant_id: string
  product_id: string
  term: string
  active: number
}

export interface ProduktZeile {
  id: string
  tenant_id: string
  code: string
  name: string
  active: number
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ProduktMitBegriffen extends ProduktZeile {
  terms: BegriffZeile[]
}

/** services/catalogue.ts, MatchTestResult */
export interface WirksamerBegriff {
  id: string
  term: string
  product_id: string
  product_code: string
  matches: boolean
}

export interface MatchTestErgebnis {
  invoice_name: string
  product: ProduktZeile | null
  effective_terms: WirksamerBegriff[]
}

/** engine/types.ts, RuleRow */
export interface RegelZeile {
  id: string
  tenant_id: string
  key: string
  name: string
  role_code: string
  role_label: string
  leistung_text: string
  product_codes_json: string | null
  recipient_mode: string
  recipient_deal_role: string | null
  recipient_tc_id: string | null
  amount_mode: string
  amount_cents: number | null
  rate_bp: number | null
  applies_to: string
  conditions_json: string
  unassigned_policy: 'emit' | 'skip'
  valid_from: string
  valid_to: string | null
  sort_order: number
  created_by: string | null
  created_at: string
}

/** services/catalogue.ts, UpsertRuleResult — plus das neu_berechnet der Route (tenant.ts). */
export interface RegelErgebnis {
  regel: RegelZeile
  modus: 'insert' | 'update_in_place' | 'versioned'
  closed_rule_id: string | null
  neu_berechnet: boolean
}

/** services/catalogue.ts, DryRunLine/DryRunResult — was ein Speichern ändern würde. */
export interface ProbeZeile {
  computation_key: string | null
  commission_id: string | null
  rule_id: string | null
  role_label: string
  recipient_tc_id: string | null
  delta_id: string | null
  amount_cents: number
}

export interface ProbeErgebnis {
  modus: 'insert' | 'update_in_place' | 'versioned'
  neu: ProbeZeile[]
  entfallen: ProbeZeile[]
  rueckbuchungen: ProbeZeile[]
  summe_neu_cents: number
  summe_entfallen_cents: number
  summe_rueckbuchungen_cents: number
}

/** services/team.ts, TeamRow */
export interface TeamZeile {
  tenant_contractor_id: string
  contractor_id: string
  display_name: string
  short_code: string
  legacy_name: string | null
  kreditor_no: number | null
  active: number
  invoice_prefix: string
  vat_mode: 'kleinunternehmer' | 'regelbesteuert' | 'reverse_charge'
  verified_at: string | null
  owner_email: string | null
  invited_at: string | null
  invited_email: string | null
}

/** services/team.ts, ContractorSnapshot — genau die Felder, die eine Rechnung braucht. */
export interface EmpfaengerStammdaten {
  tenant_contractor_id: string
  contractor_id: string
  display_name: string
  short_code: string
  legacy_name: string | null
  kreditor_no: number | null
  active: number
  legal_name: string | null
  legal_form: string | null
  representative_name: string | null
  address: unknown
  country: string
  vat_mode: 'kleinunternehmer' | 'regelbesteuert' | 'reverse_charge'
  vat_id: string | null
  tax_number: string | null
  iban: string | null
  bic: string | null
  account_holder: string | null
  invoice_prefix: string
  verified_at: string | null
  hat_login: boolean
}

/** services/tenants.ts, publicTenant() — nie webhook_secret_hash, nie die drei Lease-Spalten. */
export interface TenantEinstellungen {
  id: string
  slug: string
  name: string
  legal_name: string
  address: unknown
  hl_date_format: 'd/m/yyyy'
  hl_number_format: 'en-US' | 'de-DE'
  line_customer_format: 'full_name' | 'initials_contact_id'
  /** Wohin das FinSuites-Feld host gehört (Migration 0007). */
  host_feld: 'host' | 'sonstige'
  /** 1: Provisionen erst nach Bestätigung abrechenbar (Migration 0008) */
  provisionen_bestaetigen: number
  /** FinSuites-Tags, die einen Lead extern machen, kommagetrennt (Migration 0009) */
  extern_tags: string
  vat_id: string | null
  tax_number: string | null
  handelsregister: string | null
  geschaeftsfuehrer: string | null
  contact_email: string
  billing_email: string
  reply_to_email: string
  payment_terms_days: number
  receipt_prefix: string
  account_expense: number
  account_bank: number
  kreditor_start: number
  accepts_pdf_invoices_at: string
  legal_entity_contractor_id: string | null
  created_at: string
  updated_at: string
}

/** Die beiden Ingress-URLs; das Klartext-Secret gibt es nur in dieser einen Antwort. */
export interface WebhookGeheimnis {
  webhook_secret: string
  hook_urls: { deal: string; payment: string }
}

/** services/documents.ts, DocumentRow */
export interface DokumentZeile {
  id: string
  tenant_id: string
  kind: string
  owner_contractor_id: string | null
  period: string | null
  label: string | null
  r2_key: string
  sha256: string
  byte_size: number
  content_type: string
  created_at: string
}

/** services/billing.ts, ContractorInvoiceRow */
export interface RechnungZeile {
  id: string
  tenant_id: string
  contractor_id: string
  tenant_contractor_id: string
  kind: 'invoice' | 'cancellation' | 'legacy_import'
  cancels_invoice_id: string | null
  status: 'draft' | 'submitted' | 'rejected' | 'approved' | 'paid' | 'cancelled'
  number: string | null
  number_seq: number | null
  number_year: number | null
  issued_at: string | null
  due_at: string | null
  service_period_from: string | null
  service_period_to: string | null
  net_cents: number
  vat_mode_snapshot: 'kleinunternehmer' | 'regelbesteuert' | 'reverse_charge' | null
  vat_rate_bp: number
  vat_cents: number
  gross_cents: number
  legal_text: string | null
  issuer_snapshot_json: string | null
  recipient_snapshot_json: string | null
  consent_text: string | null
  consent_at: string | null
  self_approved: number
  document_id: string | null
  submitted_at: string | null
  submitted_by: string | null
  decided_at: string | null
  decided_by: string | null
  decision_comment: string | null
  paid_at: string | null
  created_at: string
  updated_at: string
}

/** services/billing.ts, InvoiceLineRow */
export interface RechnungPosition {
  id: string
  tenant_id: string
  invoice_id: string
  commission_id: string
  position: number
  description: string
  amount_cents: number
  references_invoice_id: string | null
  legacy_ausz_id: string | null
}

/** services/billing.ts, PayoutRow */
export interface AuszahlungZeile {
  id: string
  tenant_id: string
  invoice_id: string
  amount_cents: number
  paid_at: string
  method: string
  bank_reference: string | null
  receipt_number: string | null
  receipt_seq: number | null
  receipt_year: number | null
  receipt_document_id: string | null
  receipt_emailed_at: string | null
  recorded_by: string
  created_at: string
}

/** services/billing.ts, BillingLine — eine wählbare Zeile im Entwurf. */
export interface AbrechnungZeile {
  commission_id: string
  ref_no: number
  role_code: string
  role_label: string
  leistung_text: string
  kind: string
  status: string
  rate_nr: number | null
  amount_cents: number
  customer_name: string | null
  customer_label: string | null
  product_code: string | null
  product_name: string | null
  invoice_number: string | null
  leistungsdatum: string | null
  signed_at: string | null
  description: string
  references_invoice_id: string | null
  ausgewaehlt: boolean
  abrechenbar: boolean
  /** positive Zeile, die der Auftraggeber noch nicht bestätigt hat */
  wartet_auf_bestaetigung: boolean
}

/**
 * Was zum Einreichen noch fehlt. Kein Nachbau: der Typ wird aus der einen Quelle re-exportiert, die
 * der Server benutzt (`profil: contractorProfileComplete(...)` in services/billing.ts). Das Feld
 * heißt dort `complete`, nicht `vollstaendig` — eine eigene Beschreibung hier wäre genau die Drift,
 * gegen die dieses System gebaut ist. `src/shared` wird von beiden TS-Projekten kompiliert, der
 * Re-Export ist deshalb in Worker wie Client gültig.
 */
export type ProfilVollstaendigkeit = ProfileCompleteness

/**
 * services/billing.ts, AbrechnungView — der Entwurf, wie ihn zwei Routen liefern:
 * `POST /api/me/rechnungen` antwortet mit dieser Ansicht selbst, `GET /api/me/abrechnung` mit
 * `{ abrechnung: … }`. Nur die Leseroute hat also den Umschlag.
 */
export interface AbrechnungAnsicht {
  tenant_id: string
  tenant_name: string
  tenant_contractor_id: string
  contractor_id: string
  empfaenger: string
  vat_mode: 'kleinunternehmer' | 'regelbesteuert' | 'reverse_charge'
  geprueft: boolean
  profil: ProfilVollstaendigkeit
  /** Der Satz, den der Server beim Einreichen einfriert — services/billing.ts, consentText(). */
  consent_preview: string
  invoice_id: string | null
  lines: AbrechnungZeile[]
  net_cents: number
  vat_rate_bp: number
  vat_cents: number
  gross_cents: number
}

/**
 * services/billing.ts, MyInvoiceRow — die Rechnungen aller eigenen Auftragnehmer über alle
 * Auftraggeber hinweg, mit den beiden Dokument-Ids, die der Bildschirm verlinkt.
 */
export interface MeineRechnungZeile {
  id: string
  tenant_id: string
  tenant_name: string
  tenant_contractor_id: string
  contractor_id: string
  kind: 'invoice' | 'cancellation' | 'legacy_import'
  status: 'draft' | 'submitted' | 'rejected' | 'approved' | 'paid' | 'cancelled'
  number: string | null
  issued_at: string | null
  due_at: string | null
  net_cents: number
  vat_cents: number
  gross_cents: number
  decision_comment: string | null
  submitted_at: string | null
  decided_at: string | null
  paid_at: string | null
  self_approved: number
  document_id: string | null
  beleg_document_id: string | null
  payout_paid_at: string | null
  payout_amount_cents: number | null
  created_at: string
}

/** services/billing.ts, TenantInvoiceListRow — die Freigabe-Warteschlange des Auftraggebers. */
export interface RechnungListenZeile {
  id: string
  tenant_contractor_id: string
  contractor_id: string
  kind: 'invoice' | 'cancellation' | 'legacy_import'
  status: 'draft' | 'submitted' | 'rejected' | 'approved' | 'paid' | 'cancelled'
  number: string | null
  issued_at: string | null
  due_at: string | null
  empfaenger: string
  short_code: string
  net_cents: number
  vat_cents: number
  gross_cents: number
  self_approved: number
  /** 1, solange hinter der Rechnung eine Rückbuchung wartet */
  rueckbuchung: number
  document_id: string | null
  submitted_at: string | null
  decided_at: string | null
  paid_at: string | null
  created_at: string
}

/**
 * services/billing.ts, TenantInvoiceRow — die Zeile ohne die beiden eingefrorenen JSON-Blobs, dafür
 * mit den beiden Namen, die der Dialog in seine Kopfzeile setzt.
 */
export interface RechnungDetailZeile
  extends Omit<RechnungZeile, 'issuer_snapshot_json' | 'recipient_snapshot_json'> {
  empfaenger: string
  short_code: string
  rueckbuchung: number
}

/** services/billing.ts, TenantInvoiceDetail — die Rechnung, über die die vier Dialoge entscheiden. */
export interface RechnungDetail {
  rechnung: RechnungDetailZeile
  positionen: RechnungPosition[]
  /** das geparste `issuer_snapshot_json`; null, solange die Freigabe es nicht eingefroren hat */
  issuer: IssuerSnapshot | null
  recipient: RecipientSnapshot | null
}

/** routes/admin.ts, GET /api/admin/tenants */
export interface AdminTenantZeile {
  id: string
  slug: string
  name: string
  legal_name: string
  created_at: string
}

/** routes/admin.ts, GET /api/admin/system */
export interface SystemProTenant {
  tenant_id: string
  slug: string
  pending_events: number
  failed_events: number
  queued_emails: number
  failed_emails: number
  invoices_without_document: number
  last_invariant_run: { at: string; ok: boolean; failed: string[] } | null
  last_backup: { at: string; r2_key: string; byte_size: number } | null
}

export interface SystemStatus {
  git_sha: string
  tenants: number
  pending_events: number
  failed_events: number
  queued_emails: number
  failed_emails: number
  invoices_without_document: number
  d1_size_bytes: number | null
  per_tenant: SystemProTenant[]
}

// ---------------------------------------------------------------------------------------------------
// Die Routen
// ---------------------------------------------------------------------------------------------------

export const api = {
  // -------------------------------------------------------------------------------------------------
  // Der rohe Transport. Die benannten Helfer darunter sind der bevorzugte Weg — sie tragen die
  // Körper- und Antworttypen —, aber jeder Bildschirm der Parts 24–27 baut auf genau diesen drei
  // Methoden auf: sie sind die eine Stelle, an der ein Pfad zu einer Anfrage wird.
  // -------------------------------------------------------------------------------------------------

  /** GET auf einen relativen Pfad; gesetzte Query-Werte werden angehängt, leere weggelassen. */
  get: <T>(pfad: string, query?: Query): Promise<T> => holen<T>(pfad, query),
  /** POST mit JSON-Körper; ohne Körper geht `{}` hinaus, sonst antwortet der CSRF-Wächter 415. */
  post: <T>(pfad: string, body: unknown = {}, query?: Query): Promise<T> => senden<T>(pfad, body, query),
  /** PATCH mit JSON-Körper. */
  patch: <T>(pfad: string, body: unknown): Promise<T> => aendern<T>(pfad, body),
  /** DELETE; antwortet, was der Server daraus gemacht hat (etwa `{ geloescht }`). */
  delete: <T>(pfad: string): Promise<T> => loeschen<T>(pfad),

  auth: {
    /** POST /api/auth/code — antwortet immer 200, bekannt oder nicht (spec §1.11). */
    code: (body: LoginCodeRequest): Promise<{ ok: true }> => senden('/api/auth/code', body),
    /** POST /api/auth/verify — { code_id, code } aus dem Magic-Link oder { email, code } getippt. */
    verify: (body: LoginVerify): Promise<{ user: Benutzer }> => senden('/api/auth/verify', body),
    /** POST /api/auth/invite/accept — nach der Anmeldung mit der eingeladenen Adresse. */
    einladungAnnehmen: (body: InviteAccept): Promise<EinladungErgebnis> =>
      senden('/api/auth/invite/accept', body),
    logout: (): Promise<{ ok: true }> => senden('/api/auth/logout'),
    me: (): Promise<Sitzung> => holen('/api/auth/me'),
  },

  me: {
    provisionen: (query?: MyCommissionsQuery): Promise<{ provisionen: MeineProvisionZeile[] }> =>
      holen('/api/me/provisionen', query as Query | undefined),
    pipeline: (): Promise<{ pipeline: PipelineZeile[] }> => holen('/api/me/pipeline'),
    profil: (contractorId: string): Promise<{ profil: EigenesProfil }> =>
      holen(`/api/me/profil/${teil(contractorId)}`),
    profilAendern: (contractorId: string, body: ProfilPatch): Promise<{ profil: EigenesProfil }> =>
      aendern(`/api/me/profil/${teil(contractorId)}`, body),
    /** GET /api/me/rechnungen — jede Rechnung jedes eigenen Auftragnehmers, über alle Auftraggeber. */
    rechnungen: (): Promise<{ rechnungen: MeineRechnungZeile[] }> => holen('/api/me/rechnungen'),
    /** GET /api/me/abrechnung — der Bildschirm „Jetzt abrechnen" für genau einen Auftraggeber. */
    abrechnung: (tenantContractorId: string): Promise<{ abrechnung: AbrechnungAnsicht }> =>
      holen('/api/me/abrechnung', { tenant_contractor_id: tenantContractorId }),
    /** POST /api/me/rechnungen — der idempotente Upsert des einen Entwurfs (spec §1.12). */
    entwurfSpeichern: (body: EntwurfBody): Promise<AbrechnungAnsicht> =>
      senden('/api/me/rechnungen', body),
    /**
     * Die gewässerzeichnete Vorschau (spec §1.10 Schritt 2) — Bytes, also eine Adresse für <iframe>
     * oder <a>, kein JSON. Sie speichert nichts: keine Nummer, kein Dokument, kein R2-Objekt.
     */
    vorschauUrl: (invoiceId: string): string => `/api/me/rechnungen/${teil(invoiceId)}/preview.pdf`,
    einreichen: (invoiceId: string, body: SubmitBody): Promise<{ invoice: RechnungZeile; lines: RechnungPosition[] }> =>
      senden(`/api/me/rechnungen/${teil(invoiceId)}/submit`, body),
    zurueckziehen: (invoiceId: string): Promise<{ invoice: RechnungZeile }> =>
      senden(`/api/me/rechnungen/${teil(invoiceId)}/withdraw`),
  },

  t: (tenantId: string) => {
    const basis = `/api/t/${teil(tenantId)}`
    return {
      dashboard: (query?: DashboardQuery): Promise<Dashboard> =>
        holen(`${basis}/dashboard`, query as Query | undefined),

      pruefen: (query?: InboxQuery): Promise<{ items: PruefenEintrag[] }> =>
        holen(`${basis}/inbox`, query as Query | undefined),
      /** Erledigt heißt: aus der Liste nehmen, mit Begründung — nichts an den Daten ändert sich. */
      pruefenErledigt: (kind: string, subjectId: string, body: Dismiss): Promise<{ ok: true }> =>
        senden(`${basis}/inbox/${teil(kind)}/${teil(subjectId)}/dismiss`, body),

      abschluesse: (query?: DealsQuery): Promise<{ abschluesse: AbschlussZeile[] }> =>
        holen(`${basis}/abschluesse`, query as Query | undefined),
      abschlussAnlegen: (body: CreateDealDirect): Promise<AbschlussAusZahlungErgebnis> => senden(`${basis}/abschluesse`, body),
      abschlussAendern: (dealId: string, body: AbschlussPatch): Promise<AbschlussErgebnis> =>
        aendern(`${basis}/abschluesse/${teil(dealId)}`, body),

      zahlungen: (query?: PaymentsQuery): Promise<{ zahlungen: ZahlungZeile[] }> =>
        holen(`${basis}/zahlungen`, query as Query | undefined),
      zahlungAendern: (cinId: string, body: ZahlungPatch): Promise<ZahlungErgebnis> =>
        aendern(`${basis}/zahlungen/${teil(cinId)}`, body),
      zahlungVonHand: (body: ManualPayment): Promise<HandZahlungErgebnis> => senden(`${basis}/zahlungen/manuell`, body),
      abschlussAusZahlung: (cinId: string, body: CreateDeal): Promise<AbschlussAusZahlungErgebnis> =>
        senden(`${basis}/zahlungen/${teil(cinId)}/create-deal`, body),
      rueckzahlungBestaetigen: (cinId: string, deltaId: string, body: RefundDecision): Promise<RueckzahlungErgebnis> =>
        senden(`${basis}/zahlungen/${teil(cinId)}/refund/${teil(deltaId)}/confirm`, body),
      rueckzahlungIgnorieren: (cinId: string, deltaId: string, body: RefundDecision): Promise<RueckzahlungErgebnis> =>
        senden(`${basis}/zahlungen/${teil(cinId)}/refund/${teil(deltaId)}/ignore`, body),

      provisionen: (query?: CommissionsQuery): Promise<{ provisionen: ProvisionZeile[] }> =>
        holen(`${basis}/provisionen`, query as Query | undefined),
      korrekturAnlegen: (body: Adjustment): Promise<KorrekturErgebnis> => senden(`${basis}/provisionen/adjustment`, body),
      ausbuchen: (comId: string, body: WriteOff): Promise<AusbuchungErgebnis> =>
        senden(`${basis}/provisionen/${teil(comId)}/write-off`, body),

      /** Die Warteschlange und die eine Rechnung, über die ihre Dialoge entscheiden. */
      rechnungen: (query?: RechnungenQuery): Promise<{ rechnungen: RechnungListenZeile[] }> =>
        holen(`${basis}/rechnungen`, query as Query | undefined),
      rechnung: (invoiceId: string): Promise<RechnungDetail> =>
        holen(`${basis}/rechnungen/${teil(invoiceId)}`),
      /** Die vier Entscheidungen über eine Abrechnung (spec §1.10, Schritte 3–5). */
      rechnungFreigeben: (invoiceId: string): Promise<{ invoice: RechnungZeile }> =>
        senden(`${basis}/rechnungen/${teil(invoiceId)}/approve`),
      rechnungAblehnen: (invoiceId: string, body: RejectBody): Promise<{ invoice: RechnungZeile }> =>
        senden(`${basis}/rechnungen/${teil(invoiceId)}/reject`, body),
      auszahlungErfassen: (
        invoiceId: string,
        body: PayoutBody,
      ): Promise<{ invoice: RechnungZeile; payout: AuszahlungZeile }> =>
        senden(`${basis}/rechnungen/${teil(invoiceId)}/payout`, body),
      rechnungStornieren: (
        invoiceId: string,
        body: CancelBody,
      ): Promise<{ original: RechnungZeile; cancellation: RechnungZeile; lines: RechnungPosition[] }> =>
        senden(`${basis}/rechnungen/${teil(invoiceId)}/cancel`, body),

      produkte: (): Promise<{ produkte: ProduktMitBegriffen[] }> => holen(`${basis}/produkte`),
      produktAnlegen: (body: UpsertProductBody): Promise<{ produkt: ProduktZeile; neu_berechnet: boolean }> =>
        senden(`${basis}/produkte`, body),
      produktAendern: (
        productId: string,
        body: UpsertProductBody,
      ): Promise<{ produkt: ProduktZeile; neu_berechnet: boolean }> =>
        aendern(`${basis}/produkte/${teil(productId)}`, body),
      matchTest: (body: MatchTestBody): Promise<MatchTestErgebnis> =>
        senden(`${basis}/produkte/match-test`, body),
      begriffe: (productId: string): Promise<{ terms: BegriffZeile[] }> =>
        holen(`${basis}/produkte/${teil(productId)}/terms`),
      /** POST ersetzt die ganze Liste, PATCH ergänzt sie (routes/tenant.ts). */
      begriffeErsetzen: (
        productId: string,
        body: ReplaceTermsBody,
      ): Promise<{ terms: BegriffZeile[]; neu_berechnet: boolean }> =>
        senden(`${basis}/produkte/${teil(productId)}/terms`, body),
      begriffeErgaenzen: (
        productId: string,
        body: ReplaceTermsBody,
      ): Promise<{ terms: BegriffZeile[]; neu_berechnet: boolean }> =>
        aendern(`${basis}/produkte/${teil(productId)}/terms`, body),

      regeln: (): Promise<{ regeln: RegelZeile[] }> => holen(`${basis}/regeln`),
      regelAnlegen: (body: UpsertRuleBody): Promise<RegelErgebnis> => senden(`${basis}/regeln`, body),
      regelAendern: (ruleId: string, body: UpsertRuleBody): Promise<RegelErgebnis> =>
        aendern(`${basis}/regeln/${teil(ruleId)}`, body),
      /** Der Trockenlauf: was das Speichern ändern würde, bevor gespeichert wird (spec §1.4). */
      regelProbe: (body: DryRunRuleBody): Promise<ProbeErgebnis> => senden(`${basis}/regeln/dry-run`, body),
      /** Die Standardregeln nachträglich anlegen; idempotent je Schlüssel (services/standardregeln.ts). */
      regelnStandard: (): Promise<{ angelegt: string[]; vorhanden: string[] }> => senden(`${basis}/regeln/standard`, {}),

      team: (): Promise<{ team: TeamZeile[] }> => holen(`${basis}/team`),
      teamAnlegen: (body: CreateTeamMemberBody): Promise<{ mitglied: TeamZeile }> =>
        senden(`${basis}/team`, body),
      teamMitglied: (tcId: string): Promise<{ mitglied: EmpfaengerStammdaten }> =>
        holen(`${basis}/team/${teil(tcId)}`),
      teamAendern: (tcId: string, body: PatchTeamMemberBody): Promise<{ mitglied: TeamZeile }> =>
        aendern(`${basis}/team/${teil(tcId)}`, body),
      teamEinladen: (
        tcId: string,
        body: InviteTeamMemberBody,
      ): Promise<{ einladung_id: string; gesendet_an: string }> =>
        senden(`${basis}/team/${teil(tcId)}/invite`, body),
      /** Nur der Plattform-Administrator (routes/tenant.ts, requirePlatformAdmin). */
      profilPruefen: (tcId: string): Promise<{ mitglied: TeamZeile }> =>
        senden(`${basis}/team/${teil(tcId)}/verify-profile`),

      exporte: (query?: ExporteQuery): Promise<{ exporte: DokumentZeile[] }> =>
        holen(`${basis}/exporte`, query as Query | undefined),
      exportErstellen: (body: MonatsExport): Promise<{ dokument: DokumentZeile }> =>
        senden(`${basis}/exporte/monat`, body),

      einstellungen: (): Promise<{ tenant: TenantEinstellungen }> => holen(`${basis}/einstellungen`),
      einstellungenAendern: (body: UpdateTenantBody): Promise<{ tenant: TenantEinstellungen }> =>
        aendern(`${basis}/einstellungen`, body),
      /** Das neue Klartext-Secret steht nur in dieser Antwort und ist danach nie wieder lesbar. */
      webhookSecretErneuern: (): Promise<WebhookGeheimnis> =>
        senden(`${basis}/einstellungen/webhook-secret/rotate`),

      audit: (query?: AuditQuery): Promise<{ eintraege: AuditZeile[] }> =>
        holen(`${basis}/audit`, query as Query | undefined),
    }
  },

  admin: {
    tenants: (): Promise<{ tenants: AdminTenantZeile[] }> => holen('/api/admin/tenants'),
    tenantAnlegen: (
      body: CreateTenantBody,
    ): Promise<WebhookGeheimnis & { tenant: TenantEinstellungen }> => senden('/api/admin/tenants', body),
    ownerEinladen: (
      tenantId: string,
      body: InviteOwnerBody,
    ): Promise<{ einladung_id: string; gesendet_an: string; rolle: 'owner' | 'admin' }> =>
      senden(`/api/admin/tenants/${teil(tenantId)}/invite-owner`, body),
    auftragnehmerVerknuepfen: (
      tenantId: string,
      body: LinkContractorBody,
    ): Promise<{ tenant_contractor_id: string; neu: boolean }> =>
      senden(`/api/admin/tenants/${teil(tenantId)}/link-contractor`, body),
    /** Die Parameter stehen in der Query (routes/admin.ts), der JSON-Body bleibt trotzdem Pflicht. */
    replay: (query: { tenant: string; from?: string; to?: string }): Promise<{ replayed: number }> =>
      senden('/api/admin/replay', {}, query),
    system: (): Promise<SystemStatus> => holen('/api/admin/system'),
    testmail: (
      body: TestEmailInput,
    ): Promise<{ ok: true; outbox_id: string; to: string; template: string }> =>
      senden('/api/admin/test-email', body),
  },

  dokumente: {
    /** Die einzige Route, die Bytes liefert: als Adresse für <a> oder <iframe>. */
    url: (id: string, optionen?: { download?: boolean }): string =>
      optionen?.download === true
        ? `/api/dokumente/${teil(id)}?download=1`
        : `/api/dokumente/${teil(id)}`,
    /** Wenn die Bytes im Speicher gebraucht werden (Vorschau). Fehler kommen als ApiError. */
    laden: async (id: string): Promise<Blob> => {
      const antwort = await fetch(`/api/dokumente/${teil(id)}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!antwort.ok) {
        throw fehlerAus(
          antwort.status,
          gelesen(await antwort.text()),
          'Das Dokument konnte nicht geladen werden.',
        )
      }
      return antwort.blob()
    },
  },
} as const
